
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3t77m2p2pufcdbdknbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/itscarolinayepes/start-call', '_blank');
  };

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-[#182114] to-[#2E3F25]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: '"Distro Bold", sans-serif' }}>
            Ready to Build Your <span className="text-[#D96B33]">Dream Website?</span>
          </h2>
          <div className="w-24 h-1 bg-[#D96B33] mx-auto mb-8"></div>
          <p className="text-2xl text-[#EBEBE0]/90 max-w-3xl mx-auto font-semibold" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
            Transform your vision into a stunning digital experience.
          </p>
          <p className="text-lg text-[#EBEBE0]/70 max-w-2xl mx-auto mt-4" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
            From custom web design to complete development solutions — let's create a website that captures your brand and drives results.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Book a Call Button */}
          <div className="mb-8 text-center">
            <button
              onClick={handleBookCall}
              className="inline-flex items-center gap-3 bg-[#AFC4E4] text-[#182114] px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-[#AFC4E4]/90 transition-all duration-300 hover:scale-105 hover:shadow-xl cursor-pointer whitespace-nowrap"
              style={{ fontFamily: '"Century Gothic", sans-serif' }}
            >
              <i className="ri-calendar-line text-2xl"></i>
              Book a Call
            </button>
            <p className="text-[#EBEBE0]/60 mt-3 text-sm" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
              Prefer to talk? Schedule a quick call with us
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-[#EBEBE0]/20"></div>
            <span className="text-[#EBEBE0]/60 text-sm" style={{ fontFamily: '"Century Gothic", sans-serif' }}>or send us a message</span>
            <div className="flex-1 h-px bg-[#EBEBE0]/20"></div>
          </div>

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl p-10 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-[#182114] font-semibold mb-2" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 rounded-2xl border-2 border-[#EBEBE0] focus:border-[#D96B33] focus:outline-none transition-colors text-sm"
                  style={{ fontFamily: '"Century Gothic", sans-serif' }}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[#182114] font-semibold mb-2" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 rounded-2xl border-2 border-[#EBEBE0] focus:border-[#D96B33] focus:outline-none transition-colors text-sm"
                  style={{ fontFamily: '"Century Gothic", sans-serif' }}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="company" className="block text-[#182114] font-semibold mb-2" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                Company / Project Name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-5 py-4 rounded-2xl border-2 border-[#EBEBE0] focus:border-[#D96B33] focus:outline-none transition-colors text-sm"
                style={{ fontFamily: '"Century Gothic", sans-serif' }}
                placeholder="Your Company"
              />
            </div>

            <div className="mb-8">
              <label htmlFor="message" className="block text-[#182114] font-semibold mb-2" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                Tell Us About Your Project *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-5 py-4 rounded-2xl border-2 border-[#EBEBE0] focus:border-[#D96B33] focus:outline-none transition-colors resize-none text-sm"
                style={{ fontFamily: '"Century Gothic", sans-serif' }}
                placeholder="Share your vision, goals, and what you're looking to build..."
              ></textarea>
              {/* Fixed syntax error: corrected the fontFamily string */}
              <p className="text-sm text-[#2E3F25]/60 mt-2" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D96B33] text-white py-5 rounded-2xl text-lg font-semibold hover:bg-[#D96B33]/90 transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
              style={{ fontFamily: '"Century Gothic", sans-serif' }}
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line text-2xl animate-spin"></i>
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <i className="ri-send-plane-fill text-xl"></i>
                </>
              )}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-2xl flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                <p className="text-green-800 font-semibold" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                  Thanks! We'll get back to you soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl flex items-center gap-3">
                <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                <p className="text-red-800 font-semibold" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
                  Oops! Something went wrong. Please try again.
                </p>
              </div>
            )}
          </form>

          {/* Contact info */}
          <div className="mt-12 text-center">
            <p className="text-[#EBEBE0]/70 mb-4" style={{ fontFamily: '"Century Gothic", sans-serif' }}>
              Or reach us directly at:
            </p>
            <a
              href="mailto:hello@yepesweblab.com"
              className="inline-flex items-center gap-2 text-[#AFC4E4] text-xl font-semibold hover:text-[#D96B33] transition-colors duration-300 cursor-pointer"
              style={{ fontFamily: '"Century Gothic", sans-serif' }}
            >
              <i className="ri-mail-line text-2xl"></i>
              hello@yepesweblab.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
